import type { Balance } from '../balance';
import { lineupOf } from '../game';
import type { Rng } from '../rng';
import type { Side } from '../subs';
import type { Combatant, Game, TeamId } from '../types';

/** The AI's three levels (balance.json `coach.difficulty`). */
export type Difficulty = 'rookie' | 'pro' | 'allPro';
export const DIFFICULTIES: readonly Difficulty[] = ['rookie', 'pro', 'allPro'];

/**
 * How far ahead a level plans its PP: `snap` spends on any battle a move likely flips; `drive`
 * saves it for leverage; `game` also paces it against the clock and reads score and time.
 */
export type Horizon = 'snap' | 'drive' | 'game';

/** What every AI rule is handed: the numbers, the level's noise and horizon, and its own rng. */
export interface AiContext {
  b: Balance;
  difficulty: Difficulty;
  jitter: number;
  horizon: Horizon;
  /** The coach's own rng, never the game's: noise and picks only. */
  rng: Rng;
}

export function aiContext(difficulty: Difficulty, rng: Rng, b: Balance): AiContext {
  const d = b.coach.difficulty[difficulty];
  return { b, difficulty, jitter: d.jitter, horizon: d.horizon as Horizon, rng };
}

/**
 * One draw of the level's noise in a rule's units (`coach.ai.jitterScale`): uniform in
 * +-jitter x scale. All-Pro (jitter 0) draws nothing, so its only draws are its play picks.
 */
export function noise(ctx: AiContext, scale: number): number {
  return ctx.jitter > 0 ? ctx.rng.roll(-ctx.jitter, ctx.jitter) * scale : 0;
}

export const other = (t: TeamId): TeamId => (t === 'A' ? 'B' : 'A');

/** The team on `side` for this call. */
export const teamOn = (game: Readonly<Game>, side: Side): TeamId =>
  side === 'offense' ? game.possession : other(game.possession);

/**
 * Our chart in play and the opponent's, each Pokémon with its slot's position set. This is all
 * the AI reads of the other side: its personnel as they stand, never its call or spends.
 */
export function lineups(
  game: Readonly<Game>,
  side: Side,
): { ours: Record<string, Combatant>; theirs: Record<string, Combatant> } {
  const o = game.teams[game.possession];
  const d = game.teams[other(game.possession)];
  const offense = lineupOf(o, o.offense) as Record<string, Combatant>;
  const defense = lineupOf(d, d.defense) as Record<string, Combatant>;
  return side === 'offense'
    ? { ours: offense, theirs: defense }
    : { ours: defense, theirs: offense };
}

/** Our score minus theirs. */
export const margin = (game: Readonly<Game>, team: TeamId): number =>
  game.score[team] - game.score[other(team)];
